import React, { useState } from 'react';
import { toast } from 'sonner';
import { 
  ArrowLeft, Camera, Plus, Settings, Check, X,
  Activity, Brain, TrendingUp, Loader2
} from 'lucide-react';
import HealthTrendsDashboard from '../../../components/HealthTrendsDashboard';
import HealthInsights from '../../../components/HealthInsights';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Health View Component
const HealthView = ({
  onBack,
  token,
  healthSummary,
  healthHistory,
  healthSymptoms,
  healthMetrics,
  fetchHealthSymptoms,
  fetchHealthHistory,
  onViewInsights,
  onViewTrends
}) => {
  const [showAddSymptom, setShowAddSymptom] = useState(false);
  const [showAddMetric, setShowAddMetric] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [healthCheckResult, setHealthCheckResult] = useState(null);
  
  // Symptom form state
  const [symptomForm, setSymptomForm] = useState({
    name: '',
    severity: 'mild',
    description: ''
  });
  
  // Metric form state
  const [metricForm, setMetricForm] = useState({
    type: 'blood_pressure',
    value: '',
    unit: ''
  });
  
  const captureHealthCheckPhoto = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      
      stream.getTracks().forEach(track => track.stop());
      
      const imageData = canvas.toDataURL('image/jpeg').split(',')[1];
      
      setHealthCheckLoading(true);
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/quick-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ image_base64: imageData })
      });
      
      const data = await res.json();
      if (res.ok) {
        setHealthCheckResult(data);
        toast.success('Health check completed!');
      } else {
        toast.error(data.detail || 'Health check failed');
      }
    } catch (error) {
      toast.error('Camera access denied or error occurred');
    }
    setHealthCheckLoading(false);
  };
  
  const addSymptom = async () => {
    if (!symptomForm.name.trim()) {
      toast.error('Please enter symptom name');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/symptoms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(symptomForm)
      });
      
      if (res.ok) {
        toast.success('Symptom added!');
        setShowAddSymptom(false);
        setSymptomForm({ name: '', severity: 'mild', description: '' });
        if (fetchHealthSymptoms) fetchHealthSymptoms();
      } else {
        toast.error('Failed to add symptom');
      }
    } catch (error) {
      toast.error('Error adding symptom');
    }
  };
  
  const resolveSymptom = async (symptomId) => {
    try {
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/symptoms/${symptomId}/resolve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Symptom marked as resolved!');
        if (fetchHealthSymptoms) fetchHealthSymptoms();
      }
    } catch (error) {
      toast.error('Error resolving symptom');
    }
  };
  
  const addMetric = async () => {
    if (!metricForm.value) {
      toast.error('Please enter metric value');
      return;
    }
    
    try {
      // Build query parameters - backend expects query params, not JSON body
      const params = new URLSearchParams({
        metric_type: metricForm.type,
        value: metricForm.value,
        unit: metricForm.unit || getDefaultUnit(metricForm.type)
      });
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/health/metrics?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        toast.success('Metric logged!');
        setShowAddMetric(false);
        setMetricForm({ type: 'blood_pressure', value: '', unit: '' });
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to log metric');
      }
    } catch (error) {
      toast.error('Error logging metric');
    }
  };
  
  // Helper function to get default unit for metric type
  const getDefaultUnit = (type) => {
    const units = {
      'blood_pressure': 'mmHg',
      'weight': 'kg',
      'blood_sugar': 'mg/dL',
      'heart_rate': 'bpm',
      'sleep_hours': 'hours',
      'water_intake': 'glasses',
      'steps': 'steps',
      'temperature': '°C'
    };
    return units[type] || '';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="text-white flex items-center gap-2" data-testid="health-back-btn">
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-xl font-bold text-white">Health History</h1>
          <div className="w-20"></div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Health Summary Cards */}
        {healthSummary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-red-400">{healthSummary.active_symptoms || 0}</p>
              <p className="text-gray-400 text-sm">Active Symptoms</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">{healthSummary.total_consultations || 0}</p>
              <p className="text-gray-400 text-sm">Consultations</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{healthSummary.total_prescriptions || 0}</p>
              <p className="text-gray-400 text-sm">Prescriptions</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">{healthMetrics?.length || 0}</p>
              <p className="text-gray-400 text-sm">Metrics Logged</p>
            </div>
          </div>
        )}
        
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={captureHealthCheckPhoto}
            disabled={healthCheckLoading}
            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 shadow-lg shadow-purple-500/20 transition-all disabled:opacity-50"
            data-testid="quick-health-check-btn"
          >
            {healthCheckLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            Quick Health Check
          </button>
          <button
            onClick={onViewInsights}
            className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-xl hover:bg-violet-500/30"
            data-testid="view-insights-btn"
          >
            <Brain className="w-4 h-4" /> AI Insights
          </button>
          <button
            onClick={onViewTrends}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-xl hover:bg-indigo-500/30"
            data-testid="view-trends-btn"
          >
            <TrendingUp className="w-4 h-4" /> View Trends
          </button>
          <button
            onClick={() => setShowAddSymptom(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30"
          >
            <Plus className="w-4 h-4" /> Add Symptom
          </button>
          <button
            onClick={() => setShowAddMetric(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30"
          >
            <Plus className="w-4 h-4" /> Log Metric
          </button>
        </div>
        
        {/* Health Check Result */}
        {healthCheckResult && (
          <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 mb-8">
            <h3 className="text-green-400 font-bold mb-2">Health Check Result</h3>
            <p className="text-gray-300 whitespace-pre-wrap">{healthCheckResult.analysis}</p>
            <button
              onClick={() => setHealthCheckResult(null)}
              className="mt-3 text-sm text-gray-400 hover:text-white"
            >
              Dismiss
            </button>
          </div>
        )}
        
        {/* Active Symptoms */}
        {healthSymptoms && healthSymptoms.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-white mb-4">Active Symptoms</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {healthSymptoms.map(symptom => (
                <div key={symptom.id || symptom._id} className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-white">{symptom.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      symptom.severity === 'severe' ? 'bg-red-500/20 text-red-400' :
                      symptom.severity === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {symptom.severity}
                    </span>
                  </div>
                  {symptom.description && (
                    <p className="text-gray-400 text-sm mb-2">{symptom.description}</p>
                  )}
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs text-gray-500">
                      Since: {new Date(symptom.started_date || symptom.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => resolveSymptom(symptom.id || symptom._id)}
                      className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Mark Resolved
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Recent Health History */}
        <div>
          <h2 className="text-lg font-bold text-white mb-4">Recent History</h2>
          {healthHistory && healthHistory.length > 0 ? (
            <div className="space-y-4">
              {healthHistory.map((entry, idx) => (
                <div key={entry.id || idx} className="bg-slate-800/50 rounded-xl p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${
                        entry.entry_type === 'consultation' ? 'bg-blue-400' :
                        entry.entry_type === 'symptom_log' ? 'bg-red-400' :
                        'bg-green-400'
                      }`}></span>
                      <h3 className="font-bold text-white">{entry.title}</h3>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm line-clamp-3">{entry.content}</p>
                  {entry.avatar_name && (
                    <p className="text-xs text-amber-400 mt-2">With {entry.avatar_name}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No health history yet</p>
              <p className="text-sm">Consult with Dr. Priya to start tracking</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Add Symptom Modal */}
      {showAddSymptom && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Add Symptom</h2>
              <button onClick={() => setShowAddSymptom(false)} className="text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-white text-sm mb-1 block">Symptom Name</label>
                <input
                  type="text"
                  value={symptomForm.name}
                  onChange={(e) => setSymptomForm({...symptomForm, name: e.target.value})}
                  placeholder="e.g., Headache, Fever"
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg border border-slate-700"
                />
              </div>
              <div>
                <label className="text-white text-sm mb-1 block">Severity</label>
                <select
                  value={symptomForm.severity}
                  onChange={(e) => setSymptomForm({...symptomForm, severity: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg border border-slate-700"
                >
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
              </div>
              <div>
                <label className="text-white text-sm mb-1 block">Description (optional)</label>
                <textarea
                  value={symptomForm.description}
                  onChange={(e) => setSymptomForm({...symptomForm, description: e.target.value})}
                  placeholder="Describe your symptom..."
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg border border-slate-700 h-20"
                />
              </div>
              <button
                onClick={addSymptom}
                className="w-full py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg font-bold"
              >
                Add Symptom
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Metric Modal */}
      {showAddMetric && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Log Health Metric</h2>
              <button onClick={() => setShowAddMetric(false)} className="text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-white text-sm mb-1 block">Metric Type</label>
                <select
                  value={metricForm.type}
                  onChange={(e) => setMetricForm({...metricForm, type: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg border border-slate-700"
                >
                  <option value="blood_pressure">Blood Pressure</option>
                  <option value="heart_rate">Heart Rate</option>
                  <option value="blood_sugar">Blood Sugar</option>
                  <option value="weight">Weight</option>
                  <option value="temperature">Temperature</option>
                  <option value="oxygen_level">Oxygen Level</option>
                </select>
              </div>
              <div>
                <label className="text-white text-sm mb-1 block">Value</label>
                <input
                  type="text"
                  value={metricForm.value}
                  onChange={(e) => setMetricForm({...metricForm, value: e.target.value})}
                  placeholder="Enter value"
                  className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg border border-slate-700"
                />
              </div>
              <button
                onClick={addMetric}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg font-bold"
              >
                Log Metric
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthView;
